import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import { Button } from "../components/ui/button";
import axios, { API } from "@/lib/apiClient";
import { SignalTrendChart } from "../components/SignalTrendChart";
import { ThreatHorizon } from "../components/ThreatHorizon";

const TIER_LABEL = { free: "Free", pro: "Fix Manual", truth: "Truth Verifier", agency: "Sovereign Agency", sovereign_defense: "Sovereign Defense" };

export const Dashboard = () => {
  const [audits, setAudits] = useState(null);
  const [threatHorizon, setThreatHorizon] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/audits/mine/list`).then((res) => setAudits(res.data)).catch(() => setAudits([]));
    axios.get(`${API}/audits/threat-horizon/mine`).then((res) => setThreatHorizon(res.data)).catch(() => setThreatHorizon({}));
  }, []);

  const trendsByBrand = {};
  (audits || []).forEach((a) => {
    if (a.visibility_score == null) return;
    if (!trendsByBrand[a.brand_name]) trendsByBrand[a.brand_name] = [];
    trendsByBrand[a.brand_name].push(a);
  });
  const brandsWithTrend = Object.entries(trendsByBrand).filter(([, pts]) => pts.length >= 2);

  return (
    <div className="mx-auto max-w-5xl px-6 py-12" data-testid="dashboard-page">
      <div className="mb-8 flex items-center justify-between">
        <h1 className="font-heading text-3xl font-bold text-white">Your Audit History</h1>
        <Button onClick={() => navigate("/")} className="gap-2 bg-sovereign text-white hover:bg-sovereign/90" data-testid="dashboard-new-audit-btn">
          <Plus className="h-4 w-4" /> New Audit
        </Button>
      </div>

      {Object.keys(threatHorizon).length > 0 && (
        <div className="mb-10 grid gap-4 sm:grid-cols-2" data-testid="dashboard-threat-horizon">
          {Object.entries(threatHorizon).map(([brand, points]) => (
            <ThreatHorizon key={brand} brandName={brand} points={points} />
          ))}
        </div>
      )}

      {brandsWithTrend.length > 0 && (
        <div className="mb-10 grid gap-4 sm:grid-cols-2" data-testid="dashboard-trends">
          {brandsWithTrend.map(([brand, points]) => (
            <SignalTrendChart key={brand} brandName={brand} points={points} />
          ))}
        </div>
      )}

      {audits === null && <p className="text-slate-500">Loading...</p>}
      {audits?.length === 0 && <p className="text-slate-500" data-testid="dashboard-empty">No audits yet. Start your first brand scan.</p>}

      <div className="space-y-3">
        {audits
          ?.slice()
          .reverse()
          .map((a) => (
            <Link
              key={a.id}
              to={`/audit/${a.id}`}
              className="flex items-center justify-between rounded-lg border border-surface-border bg-surface-card p-5 transition-colors hover:border-sovereign/40"
              data-testid={`dashboard-audit-row-${a.id}`}
            >
              <div>
                <p className="font-heading text-lg font-semibold text-white">
                  {a.brand_name} {a.auto_scan && <span className="ml-2 rounded-full bg-openai-cyan/10 px-2 py-0.5 text-[10px] font-semibold text-openai-cyan">AUTO</span>}
                </p>
                <p className="text-xs uppercase tracking-wide text-slate-500">{a.status}</p>
              </div>
              <div className="flex items-center gap-6">
                <span className="text-sm text-slate-400">{TIER_LABEL[a.paid_tier] || "Free"}</span>
                {a.visibility_score != null && <span className="font-heading text-2xl font-bold text-white">{a.visibility_score}</span>}
              </div>
            </Link>
          ))}
      </div>
    </div>
  );
};
